import ShippingId from '@/components/Shippings/ListItemId'
import ShippingExternalId from '@/components/Shippings/ListItemExternalId'
import ShippingInstructions from '@/components/Shippings/ListItemInstructions'
import ShippingsListItemStatus from '@/components/Shippings/ListItemStatus'
import ShippingCheckpoint from '@/components/Shippings/Checkpoint'
import EditFieldItems from '@/components/Shippings/EditFieldItems'
import EditFieldCourier from '@/components/Shippings/EditFieldCourier'
import { sanitizeModel } from '@/common/utils/modelDecorator'

export default {
  computed: {
    shippingsModel() {
      return sanitizeModel({
        title: 'Shippings',
        endpoint: this.$appConfig.api.baseUrl + 'carriers/' + this.profile.company_id + '/shippings',
        listExtrasComponent: () => import('@/components/Shippings/ListExtras'),
        allowSelect: true,
        countrize: 'from_country_iso_code',
        fields: [
          {
            key: 'id',
            sortable: false,
            editable: false,
            label: this.$gettext('ID'),
            component: (value, key, item) => {
              return {
                is: ShippingId,
                shipping: item
              }
            }
          },
          {
            key: 'external_code',
            label: this.$gettext('External ID'),
            component: (value, key, item) => {
              return {
                is: ShippingExternalId,
                shipping: item
              }
            }
          },
          {
            key: 'from_contact',
            sortable: true,
            label: this.$gettext('Origin'),
            class: 'col-contact',
            component: (value, key, item) => {
              return {
                is: ShippingCheckpoint,
                shipping: item,
                checkpoint: 'from',
                withEdit: !this.ecosystemConfig.disableEditOrigins,
                withIcons: true,
                withTooltip: true,
                withExtraCheckpoints: true
              }
            }
          },
          {
            key: 'from_address',
            sortable: false,
            label: this.$gettext('Origin Address')
          },
          {
            key: 'to_contact',
            sortable: false,
            label: this.$gettext('Destination'),
            class: 'col-contact',
            component: (value, key, item) => {
              return {
                is: ShippingCheckpoint,
                shipping: item,
                checkpoint: 'to',
                withEdit: !this.ecosystemConfig.disableEditDestinations,
                withIcons: true,
                withTooltip: true,
                withExtraCheckpoints: true
              }
            }
          },
          {
            key: 'to_address',
            sortable: false,
            label: this.$gettext('Destination Address')
          },
          {
            key: 'instructions',
            sortable: false,
            label: this.$gettext('Instruct.'),
            component: (value, key, item) => {
              return {
                is: ShippingInstructions,
                shipping: item
              }
            }
          },
          {
            key: 'scheduled_date',
            sortable: true,
            label: this.$gettext('Sched. At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'status',
            sortable: true,
            label: this.$gettext('Status'),
            component: (value, key, item) => {
              return {
                is: ShippingsListItemStatus,
                shipping: item
              }
            },
            class: 'text-center'
          },
          {
            key: 'courier',
            sortable: false,
            label: this.$gettext('Courier'),
            component: (value, key, item) => {
              return {
                is: 'user-card',
                user: item.courier || {},
                profilePicture: false,
                withTitles: false
                // " :vehicle-types-prop="vehicleTypes"
              }
            }
          },
          {
            key: 'size_id',
            sortable: false,
            label: this.$gettext('Size'),
            formatter: (value, key, item) => {
              return this.shippingSizes.findIndex(x => x.id === value) !== -1 ? this.shippingSizes.find(x => x.id === value).name : ''
            }
          },
          {
            key: 'shipping_type_id',
            sortable: false,
            label: this.$gettext('Type'),
            formatter: (value, key, item) => {
              let html = this.shippingTypesShared.findIndex(x => x.value === value) !== -1 ? this.shippingTypesShared.find(x => x.value === value).text : '-'
              if (item.tags && item.tags.length > 0) {
                for (const tag of item.tags) {
                  html += '<span class="badge badge-pill">' + tag + '</span>&nbsp;'
                }
              }
              return html
            }
          },
          {
            key: 'courier_commission',
            label: this.$gettext('Commission'),
            type: 'number',
            hidden: true,
            class: 'text-center',
            formatter: (value, key, item) => {
              return this.$options.filters.currency(value, window.country)
            }
          },
          {
            key: 'created_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Created At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'updated_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Updated At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          }
        ],
        extraActions: [
          {
            label: '',
            type: () => import('@/components/Shippings/ListItemActions'), // external bunch of actions
            position: 'start',
            props: (item) => {
              return {
                shipping: item
              }
            }
          }
        ],
        filters: [
          'id',
          'courier_id',
          'from_place_id',
          'from_contact',
          'address_from.formatted_address',
          'from_email',
          'from_phone',
          'to_contact',
          'address_to.formatted_address',
          'to_email',
          'to_phone',
          'status',
          'scheduled_date',
          'created_at',
          'user_id',
          'shipping_type_id',
          'tour_id'
        ],
        includes: [
          'courier.courier_available.tracking',
          'courier.roles',
          'courier.company',
          'status_history',
          'pending_hooks',
          'tags',
          'threads',
          'previousShipping',
          'followingShippings',
          'items'
        ],
        exclude: [
          'from_address',
          'to_address'
        ]
      })
    },
    shippingsAsCourierModel() {
      return sanitizeModel({
        title: 'Shippings',
        endpoint: this.$appConfig.api.baseUrl + 'carriers/' + this.profile.company_id + '/shippings',
        fields: [
          {
            key: 'id',
            label: this.$gettext('ID'),
            component: (value, key, item) => {
              return {
                is: ShippingId,
                shipping: item
              }
            }
          },
          {
            key: 'status',
            label: this.$gettext('Status'),
            sortable: true,
            component: (value, key, item) => {
              return {
                is: ShippingsListItemStatus,
                shipping: item
              }
            },
            class: 'text-center'
          },
          {
            key: 'from',
            label: this.$gettext('From'),
            formatter: (value, key, item) => {
              return item.address_from.formatted_address
            }
          },
          {
            key: 'to',
            label: this.$gettext('To'),
            formatter: (value, key, item) => {
              return item.address_to.formatted_address
            }
          },
          {
            key: 'created_at',
            label: this.$gettext('Created At'),
            sortable: true,
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'updated_at',
            label: this.$gettext('Updated At'),
            sortable: true,
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          }
        ],
        filters: [
          'id'
        ]
      })
    },
    shippingsInOrder() {
      return sanitizeModel({
        title: 'Shippings',
        endpoint: this.$appConfig.api.baseUrl + 'carriers/' + this.profile.company_id + '/shippings',
        editInPlace: true,
        listExtrasComponent: () => import('@/components/Shippings/ListInOrderExtras'),
        allowSelect: true,
        exclude: this.ecosystemModules.items?.self ? [] : ['items'],
        fields: [
          {
            key: 'id',
            label: this.$gettext('ID'),
            component: (value, key, item) => {
              return {
                is: ShippingId,
                shipping: item
              }
            }
          },
          {
            key: 'external_code',
            label: this.$gettext('External ID'),
            component: (value, key, item) => {
              return {
                is: ShippingExternalId,
                shipping: item
              }
            }
          },
          {
            key: 'status',
            label: this.$gettext('Status'),
            component: (value, key, item) => {
              return {
                is: ShippingsListItemStatus,
                shipping: item
              }
            },
            class: 'text-center'
          },
          {
            key: 'courier_commission',
            label: this.$gettext('Commission'),
            type: 'number',
            hidden: true,
            class: 'text-center',
            formatter: (value, key, item) => {
              return this.$options.filters.currency(value, window.country)
            }
          },
          {
            key: 'estimated_pickup_date',
            label: this.$gettext('Est. Pickup Date'),
            type: 'datetime',
            hidden: true,
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'pickup_note',
            label: this.$gettext('Pickup Note'),
            type: 'textarea',
            hidden: true,
            default: '',
            props: {
              rows: 3,
              maxRows: 5
            },
            formatter: (value, key, item) => {
              return this.$options.filters.brize(value)
            }
          },
          {
            key: 'estimated_delivery_date',
            label: this.$gettext('Est. Delivery Date'),
            type: 'datetime',
            hidden: true,
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'delivery_note',
            label: this.$gettext('Delivery Note'),
            type: 'textarea',
            hidden: true,
            default: '',
            props: {
              rows: 3,
              maxRows: 5
            },
            formatter: (value, key, item) => {
              return this.$options.filters.brize(value)
            }
          },
          {
            key: 'items',
            label: this.ecosystemModules.items.label || this.$gettext('Items'),
            hidden: !this.ecosystemModules.items?.self,
            formatter: (value, key, item) => {
              if (value.length > 0) {
                let html = ''
                let descriptions = []
                for (const item of value) {
                  if (item.reference_code) {
                    html += `<span class="badge mr-1 badge-dark h6">${item.reference_code}</span>`
                  } else if (item.description) {
                    descriptions.push(item.description)
                  }
                }
                if (descriptions.length > 0) {
                  descriptions = descriptions.join(', ')
                } else {
                  descriptions = ''
                }
                return html + descriptions
              }
            },
            componentEdit: (value, key, item) => {
              this.orderServicesValue = item[key]
              return {
                is: EditFieldItems,
                props: {
                  placeholder: this.$gettext('Reference, or External ID'),
                  shipping: item,
                  singleItem: true,
                  value: item?.items || []
                }
              }
            }
          },
          {
            key: 'photos',
            label: this.$gettext('Documents'),
            formatter: (value, key, item) => {
              let html = ''
              if (value) {
                const success = value.filter(x => x.content_status === 'automatic' || x.content_status === 'reviewed')
                const warning = value.filter(x => x.content_status === 'manual')
                if (!value.length) {
                  html += `<span class="badge mr-1 badge-danger" title="${this.$gettext('No Documents')}">0</span>`
                }
                if (success.length) {
                  html += `<span class="badge mr-1 badge-success" title="${this.$gettext('Automatic and Reviewed')}">${success.length}</span>`
                }
                if (warning.length) {
                  html += `<span class="badge mr-1 badge-warning" title="${this.$gettext('Need Review')}">${warning.length}</span>`
                }
              }

              return html
            }
          },
          {
            key: 'courier',
            sortable: false,
            label: this.$gettext('Courier'),
            component: (value, key, item) => {
              return {
                is: 'user-card',
                user: item.courier || {},
                profilePicture: false,
                withTitles: false
              }
            },
            componentEdit: (value, key, item) => {
              return {
                is: EditFieldCourier,
                props: {
                  endpoint: 'companies/users',
                  placeholder: this.$gettext('Reference, or External ID'),
                  shipping: item,
                  value: item?.courier || {}
                },
                on: {
                  success: (shipping) => {
                    item.courier_id = shipping.courier_id
                    item.courier = shipping.courier
                    item.status = shipping.status
                  }
                }
              }
            }
          }
        ],
        includes: [
          'items',
          'photos'
        ]
      })
    }
  }
}
